/**
 * _offline_line_items.js — what the Price editor opens with when there is no
 * network: the device's copy of a record's price and line items.
 *
 * Online the editor seeds from the page (#quoteData / #jobData, rendered by
 * Jinja with the page). Offline the page is whatever copy the device last
 * cached, and two things on the device can be newer than it:
 *
 *   1. the user's own edit of this record still waiting to upload (a
 *      powersync_write_requests row carrying line_items_json): reopening the
 *      editor shows what they saved, not what the page was drawn with;
 *   2. the record as PowerSync last synced it (powersync_quotes / _jobs and
 *      their line items, streamed for records in the user's offline_scope),
 *      when that copy is newer than the page.
 *
 * Each answer names the record version the values belong to, so the queued
 * edit is checked against the state the user actually edited from
 * (offline_intent_controller.js reads it off the form). A synced copy carries
 * no field fingerprints: the server then treats every submitted field as
 * changed if the record moved again before upload, which refuses rather than
 * overwrites.
 *
 * Returns null when neither is newer than the page, and the page seeds as
 * online. Never a renderer: the editor's own manager draws the rows.
 */

const PENDING_SQL = "SELECT r.payload, r.record_version, r.record_fields "
    + "FROM powersync_write_requests r "
    + "WHERE r.operation = ? AND r.target_id = ? "
    + "AND EXISTS (SELECT 1 FROM ps_crud c WHERE json_extract(c.data, '$.id') = r.id) "
    + "ORDER BY r.requested_at DESC";

const SYNCED = {
    quote: {
        parent: "SELECT amount, show_line_items, updated_at FROM powersync_quotes WHERE id = ?",
        items: "SELECT * FROM powersync_quote_line_items WHERE quote_id = ? ORDER BY sort_order, id",
    },
    job: {
        parent: "SELECT amount, show_line_items, updated_at FROM powersync_jobs WHERE id = ?",
        items: "SELECT * FROM powersync_job_line_items WHERE job_id = ? ORDER BY sort_order, id",
    },
};

// PowerSync ships timestamptz as UTC text, with a space or a 'T' and as many
// fraction digits as Postgres printed.
const SYNCED_STAMP = /^(\d{4}-\d{2}-\d{2})[ T](\d{2}:\d{2}:\d{2})(?:\.(\d{1,6}))?(?:Z|\+00(?::?00)?)$/;

/**
 * The record version of a synced row, spelled as the server spells it
 * (application/utils/record_version.py: UTC, microseconds always present).
 * null for a stamp it cannot read, so the page's own version stands.
 */
export function syncedRecordVersion(recordType, id, updatedAt) {
    const match = SYNCED_STAMP.exec(String(updatedAt || ""));
    if (!match) return null;
    const [, date, time, fraction = ""] = match;
    return `${recordType}:${id}:${date}T${time}.${fraction.padEnd(6, "0")}+00:00`;
}

function stampOf(version) {
    return (version || "").split(":").slice(2).join(":");
}

/** The user's latest waiting edit of this record that carries line items. */
async function pendingEdit(db, operation, id) {
    const rows = await db.getAll(PENDING_SQL, [operation, String(id)]);
    for (const row of rows) {
        let fields;
        try {
            fields = new Map(JSON.parse(row.payload || "[]"));
        } catch (_e) {
            continue;
        }
        if (!fields.has("line_items_json")) continue;
        let items = [];
        try {
            items = fields.get("use_line_items") === "true" ? JSON.parse(fields.get("line_items_json") || "[]") : [];
        } catch (_e) { /* an unreadable breakdown opens empty, as the server would read it */ }
        return {
            items,
            show: fields.get("show_line_items") === "true",
            amount: fields.get("amount") ?? null,
            version: row.record_version || "",
            fields: row.record_fields || "",
        };
    }
    return null;
}

/** The synced copy of this record, when it is newer than the page. */
async function syncedCopy(db, entityType, id, pageVersion) {
    const sql = SYNCED[entityType];
    if (!sql) return null;
    const parent = await db.getOptional(sql.parent, [String(id)]);
    const version = parent && syncedRecordVersion(entityType, id, parent.updated_at);
    if (!version || stampOf(version) <= stampOf(pageVersion)) return null;
    const rows = await db.getAll(sql.items, [String(id)]);
    return {
        items: rows.map((row) => ({ ...row, is_taxable: !!row.is_taxable })),
        show: !!parent.show_line_items,
        amount: parent.amount ?? null,
        version,
        fields: "",
    };
}

/**
 * What the Price editor for ``entityType`` ``id`` opens with offline, or null
 * to seed from the page. ``operation`` is the editor's offline operation.
 */
export async function deviceLineItems({ entityType, id, operation }) {
    const db = window.chippiePowerSync;
    if (!db || navigator.onLine) return null;
    try {
        return await pendingEdit(db, operation, id)
            ?? await syncedCopy(db, entityType, id, document.body.dataset.recordVersion);
    } catch (error) {
        console.error("[offline-line-items] could not read the device copy", error);
        return null;
    }
}
